import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import Landing from "./pages/Landing";
import { LangProvider, langFromPath, pathForLang } from "./i18n";
import { decideAutoRedirect, getStoredPref } from "./lib/langPref";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { useHideEmergentBadge } from "./hooks/useHideEmergentBadge";

const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Admin = lazy(() => import("./pages/Admin"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Insights = lazy(() => import("./pages/Insights"));
const Article = lazy(() => import("./pages/Article"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));

const Wrap = ({ lang, children }) => (
  <LangProvider lang={lang} key={lang}>{children}</LangProvider>
);

/** Auto-redirect first-time visitors to their browser language (once). */
const useLangAutoRedirect = () => {
  const loc = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    // Skip auth/admin/oauth flows entirely.
    const skipPrefixes = ["/admin", "/auth"];
    if (skipPrefixes.some((p) => loc.pathname.startsWith(p))) return;
    if (loc.hash && loc.hash.includes("session_id=")) return;
    if (getStoredPref()) return; // user has chosen — never override
    const currentLang = langFromPath(loc.pathname);
    const target = decideAutoRedirect(currentLang);
    if (!target) return;
    const newPath = pathForLang(target, loc.pathname) + loc.hash;
    navigate(newPath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const RouteSwitch = () => {
  const location = useLocation();
  useHideEmergentBadge();
  useLangAutoRedirect();
  // Synchronous detect of OAuth callback fragment — must beat any /auth/me race.
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={null}>
        <AuthCallback />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={null}>
      <Routes>
        {/* English (default) */}
        <Route path="/" element={<Wrap key="lang-en-home" lang="en"><Landing /></Wrap>} />
        <Route path="/about" element={<Wrap key="lang-en-about" lang="en"><About /></Wrap>} />
        <Route path="/contact" element={<Wrap key="lang-en-contact" lang="en"><Contact /></Wrap>} />
        <Route path="/insights" element={<Wrap key="lang-en-insights" lang="en"><Insights /></Wrap>} />
        <Route path="/insights/:slug" element={<Wrap key="lang-en-article" lang="en"><Article /></Wrap>} />

        {/* Spanish */}
        <Route path="/es" element={<Wrap key="lang-es-home" lang="es"><Landing /></Wrap>} />
        <Route path="/es/about" element={<Wrap key="lang-es-about" lang="es"><About /></Wrap>} />
        <Route path="/es/contact" element={<Wrap key="lang-es-contact" lang="es"><Contact /></Wrap>} />
        <Route path="/es/insights" element={<Wrap key="lang-es-insights" lang="es"><Insights /></Wrap>} />
        <Route path="/es/insights/:slug" element={<Wrap key="lang-es-article" lang="es"><Article /></Wrap>} />

        {/* Admin (always EN-only) */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />

        {/* Catch-all → home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RouteSwitch />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
